import BackButton from "@components/buttons/BackButton";
import CustomButton from "@components/buttons/CustomButton";
import CustomDateField from "@components/form/CustomDateField";
import CustomFileField from "@components/form/CustomFileField";
import CustomInputField from "@components/form/CustomInputField";
import CustomSelectField from "@components/form/CustomSelectField";
import CustomTextAreaField from "@components/form/CustomTextAreaField";
import { LoadingSpinner } from "@components/layouts/LoadingSpinner";
import PageWrapper from "@components/layouts/PageWrapper";
import { Toast } from "@helpers/popups/Toast";
import { ZNews, newsSchema } from "@helpers/validation-schemas/cms/news.schema";
import { zodResolver } from "@hookform/resolvers/zod";
import useGetPermission from "@hooks/common/useGetPermission";
import { useErrorHandling } from "@hooks/useErrorHandling";
import { useGetNewsQuery, useUpdateNewsMutation } from "@states/cms/news.api";
import { TFailResponse } from "@type-defs/general/TFailResponse";
import { TSuccessResponse } from "@type-defs/general/TSuccessResponse";
import { NEWS } from "@utils/constants/cms/news.constants";
import { castTypeArr } from "@utils/general/cast-type";
import React, { useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { Navigate, useNavigate, useParams } from "react-router-dom";

const serializeSocialLinks = (links?: { label?: string; url: string }[]) => {
  if (!links?.length) return "";

  return links
    .map((link) => (link.label ? `${link.label}|${link.url}` : link.url))
    .join("\n");
};

const parseSocialLinksInput = (value?: string) => {
  if (!value?.trim()) return [];

  return value
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [rawLabel, ...rawUrl] = line.split("|");

      if (rawUrl.length === 0) {
        return {
          label: "",
          url: rawLabel.trim(),
        };
      }

      return {
        label: rawLabel.trim(),
        url: rawUrl.join("|").trim(),
      };
    })
    .filter((item) => item.url);
};

const toApiDate = (value: unknown) => {
  if (!value) return "";
  const date = new Date(value as string | Date);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().split("T")[0];
};

function NewsEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasAccess } = useGetPermission();
  const canEdit =
    hasAccess("edit", "CMS", "News") || hasAccess("edit", "Others", "News");
  const { data: newsData } = useGetNewsQuery({
    id: id || "",
  });
  const [updateNews, { isLoading, error: updateError }] =
    useUpdateNewsMutation();
  const {
    register,
    handleSubmit,
    setError,
    setValue,
    watch,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ZNews>({
    resolver: zodResolver(newsSchema),
    defaultValues: {
      status: 1,
      social_links_text: "",
    },
  });

  useEffect(() => {
    const item = newsData?.data;
    if (item) {
      reset({
        title: item.title,
        content: item.content,
        from_date: item.from_date,
        to_date: item.to_date,
        status: item.status,
        social_links_text: serializeSocialLinks(item.social_links),
      });
    }
  }, [newsData, reset]);

  useErrorHandling(...castTypeArr<TFailResponse>([updateError]));

  const onSubmit: SubmitHandler<ZNews> = async (data) => {
    try {
      const formData = new FormData();
      formData.append("title", data.title);
      formData.append("content", data.content);

      const fromDate = toApiDate(data.from_date);
      const toDate = toApiDate(data.to_date);

      if (fromDate) {
        formData.append("from_date", fromDate);
      }

      if (toDate) {
        formData.append("to_date", toDate);
      }

      if (data.status !== undefined && data.status !== null) {
        formData.append("status", String(data.status));
      }

      formData.append(
        "social_links",
        JSON.stringify(parseSocialLinksInput(data.social_links_text))
      );

      const image = (data as any).image?.[0];
      if (image) {
        formData.append("image", image);
      }

      const res = await updateNews({
        id,
        body: formData,
      });

      const successData = "data" in res ? (res.data as TSuccessResponse) : null;

      if (successData?.success) {
        await Toast.fire({
          title: successData.message,
          icon: "success",
        });

        navigate(NEWS.BASE_PATH, {
          state: {
            reload: true,
          },
        });
      }
    } catch (error: any) {
      setError("root", {
        message: error.message,
      });
    }
  };

  if (!canEdit) {
    return <Navigate to="/access-denied" />;
  }

  return (
    <PageWrapper>
      {isLoading && <LoadingSpinner />}
      <BackButton />
      <form onSubmit={handleSubmit(onSubmit)}>
        <h3 className="mb-4 ml-2 text-2xl font-[600] uppercase text-primary-dark">
          {NEWS.EDIT_NEW_TITLE}
        </h3>

        <div className="grid grid-cols-6 gap-x-4 gap-y-8">
          <CustomInputField
            label="Title"
            errors={errors}
            name="title"
            placeHolder="Enter title"
            register={register}
            required
            colSpan={3}
          />
          <CustomDateField
            name="from_date"
            label="Start Date"
            control={control as any}
            register={register}
            errors={errors}
            colSpan={1}
          />
          <CustomDateField
            name="to_date"
            label="End Date"
            control={control as any}
            register={register}
            errors={errors}
            colSpan={1}
          />
          <CustomSelectField
            errors={errors}
            name="status"
            label="Status"
            control={control as any}
            options={[
              { label: "Active", value: 1 },
              { label: "Inactive", value: 0 },
            ]}
            value={1}
            colSpan={1}
          />
        </div>

        <div className="grid grid-cols-6 gap-x-4 gap-y-8">
          <CustomTextAreaField
            label="Content"
            errors={errors}
            name="content"
            placeHolder="Enter content"
            register={register}
            required
            colSpan={6}
            rows={5}
          />
        </div>

        <div className="grid grid-cols-6 gap-x-4 gap-y-8">
          <CustomTextAreaField
            label="Social Links"
            errors={errors}
            name="social_links_text"
            placeHolder="One per line. Use label|url or just url"
            register={register}
            colSpan={6}
            rows={4}
          />
        </div>

        <div className="grid grid-cols-6 gap-x-4 gap-y-8">
          <CustomFileField
            label="Cover Image"
            errors={errors}
            name="image"
            register={register}
            setValue={setValue}
            watch={watch}
            colSpan={3}
            value={
              newsData?.data?.image_url ||
              newsData?.data?.image ||
              newsData?.data?.cover_image
            }
          />
        </div>

        <div className="ml-2 mt-14">
          <CustomButton
            isLarge
            title="Cancel"
            color="white"
            textColor="gray.800"
            className="mr-10"
            handleClick={() => navigate(-1)}
          />
          <CustomButton
            handleClick={handleSubmit(onSubmit)}
            isLarge
            title={isSubmitting ? "Loading..." : "Submit"}
            disabled={isSubmitting}
            type="submit"
          />
        </div>

        {errors.root && (
          <div className="mt-6 text-red-500">{errors.root.message}</div>
        )}
      </form>
    </PageWrapper>
  );
}

export default NewsEditPage;
